import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { PageConfigurationProvider } from '@app/finxone-web-frontend/app/Modules/zones/page-configuration.provider';
import { ConfigService } from '@app/finxone-web-frontend/app/lib/services/config-service/config-service.service';
import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import { PasswordService } from '@app/finxone-web-frontend/app/lib/services/password-service/password-service.service';
import {
  BaseWidgetProperties,
  ProviderClass,
  WidgetNames,
  baseCssConfigStyle,
  buttonNestedWidgetConfigStyles,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { DeviceDetectorService } from 'ngx-device-detector';
import { FormActionTypeEnum } from '../../../../../../../../../libs/shared/enums/form-action.enum';
import {
  FormStage,
  MultiPartFormHolderService,
} from '../../../../../lib/services/multipart-holder/multipart-form-holder.service';
import { ZonePageUrlProvider } from '../../../../zones/zone-url-provider';
import { FormSubmitter } from '../../form-widgets/form-submitter/form-submitter.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-password-enter-email',
  templateUrl: './password-enter-email-configuration.component.html',
})
export class PasswordEnterEmailComponent extends FormSubmitter implements OnInit {
  public buttonContent = '';
  public renderedButtonContent = '';
  public zone = '';
  public enableSubmit = false;
  public nextPageUrl: string;
  public passwordForm: FormGroup;
  public reviewDetailsButtonStyle: baseCssConfigStyle = {};
  public deviceType: string;
  private inputActiveStyle: baseCssConfigStyle;
  private inputInactiveStyle: baseCssConfigStyle;
  public inputStyle: baseCssConfigStyle;
  public globalStylingClass = '';
  public isAllowedGlobalStyling = false;

  constructor(
    private _router: Router,
    private deviceService: DeviceDetectorService,
    private breakpointObserver: BreakpointObserver,
    public dataIn: ProviderClass<any>,
    public pageConfig: PageConfigurationProvider,
    public override store: Store,
    private configService: ConfigService,
    private formSubmissionService: FormSubmissionService,
    public formHolderService: MultiPartFormHolderService,
    private urlZoneInput: ZonePageUrlProvider,
    private passwordService: PasswordService,
  ) {
    super(store);
    this.initializeComponent();
  }

  ngOnInit(): void {
    this.createFormStage();
    this.buildForm();
    this.setButtonContentAndStyle();
    this.getGlobalStyling();
  }

  private getGlobalStyling() {
    if (
      this.dataIn.widgetConfig.nestedWidgets?.['reviewDetailsButton'].widgetProperties?.[
        'globalStylingClass'
      ] &&
      this.dataIn.widgetConfig.nestedWidgets?.['reviewDetailsButton'].widgetProperties?.['globalStyling']
    ) {
      this.globalStylingClass =
        this.dataIn.widgetConfig.nestedWidgets?.['reviewDetailsButton'].widgetProperties?.[
          'globalStylingClass'
        ] ?? '';
      this.isAllowedGlobalStyling =
        this.dataIn.widgetConfig.nestedWidgets?.['reviewDetailsButton'].widgetProperties?.['globalStyling'] ??
        false;
    }
  }

  private initializeComponent(): void {
    this.contentStyle = this.configService.onGetBaseWidgetStyle(this.dataIn.attri);
    this.getAndAssignInputFieldStyles(this.dataIn.attri.widgetProperties);
    this.zone = this.urlZoneInput.zoneUrlInput;
  }

  private createFormStage(): void {
    const formStage = new FormStage({
      position: 0,
      name: WidgetNames.PASSWORD_ENTER_EMAIL,
      route: '',
      form: new FormGroup({
        email: new FormControl('', [Validators.required, Validators.email]),
      }),
    });
    this.formSubmissionService.addCurrentPageFormGroups(formStage.form);
    this.formHolderService.config.addStage(formStage);
  }

  private buildForm(): void {
    const stage = this.formHolderService.config.getStage(WidgetNames.PASSWORD_ENTER_EMAIL);
    this.passwordForm = stage.getForm();
  }

  private setButtonContentAndStyle(): void {
    this.buttonContent = this.getButtonContent();
    this.reviewDetailsButtonStyle = this.configService.getSpecificBaseCssStyle(
      this.getNestedWidgetConfig(this.dataIn, 'reviewDetailsButton'),
      buttonNestedWidgetConfigStyles,
    );
  }

  private getButtonContent(): string {
    return (
      this.dataIn.widgetConfig.nestedWidgets?.['reviewDetailsButton']?.widgetProperties.textContent ??
      'Send Link'
    );
  }

  override getNestedWidgetConfig(dataIn: ProviderClass<any>, uniqueId: string): any {
    return dataIn.widgetConfig.nestedWidgets?.[uniqueId] ?? {};
  }

  public onSubmit(): void {
    if (this.passwordForm.valid) {
      const formValues: Record<string, any> = this.passwordForm.value;

      this.formSubmissionService
        .submitIndividualForm(formValues, '', FormActionTypeEnum.PASWORD_CHANGE)
        .then((success) => {
          if (success) {
            const { email } = formValues;

            this.passwordService.resetPasswordEmailOnly(email).subscribe({
              next: () => {
                this._router.navigate([`/zones/forgot-password/password-check-email`], {
                  queryParams: { email },
                });
              },
              error: (err) => {
                console.error('Error sending reset password email:', err);
              },
            });

            let name = '';
            Object.values(formValues).forEach((value: string) => {
              name = name + value + ' ';
            });
          }
        });
    } else {
      this.passwordForm.markAllAsTouched();
    }
  }

  private handlePasswordReset(formValues: Record<string, any>): void {
    const { email } = formValues;

    this.passwordService.resetPasswordEmailOnly(email).subscribe({
      next: () => {
        this._router.navigate(['/zones/forgot-password/check-email']);
      },
      error: (err) => {
        console.error('Error sending reset password email:', err);
      },
    });
  }

  private getAndAssignInputFieldStyles(widgetProperties: BaseWidgetProperties) {
    const { baseStyle, activeStyle, inactiveStyle } =
      this.configService.getInputFieldCssStyles(widgetProperties);
    this.inputActiveStyle = {
      ...baseStyle,
      ...activeStyle,
    };
    this.inputInactiveStyle = {
      ...inactiveStyle,
      ...activeStyle,
    };
    this.inputStyle = this.inputInactiveStyle;
  }

  public updateInputFieldCurrentStyles(event: boolean) {
    this.inputStyle = event ? this.inputActiveStyle : this.inputInactiveStyle;
  }
}
